import { XMarkIcon } from "@heroicons/react/20/solid";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base } from "@/atoms-react/text/Text.react";
import { Themes } from "@/types/theme";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useSearchParams } from "react-router-dom";
import { ModalDeleteAlias } from "../modal-delete-alias/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export type DocumentAction = "SIGNED" | "REFUSED" | "cancel" | null;

type ConsentDocumentProps = {
  padding?: boolean;
  onDocumentAction: ({
    documentConsent,
    documentAction,
    comment
  }: {
    documentConsent: string;
    documentAction: DocumentAction;
    comment?: string;
  }) => void;
  contentBase64: string;
  isPendingConfirmDocument: boolean;
  isPendingRejectDocument: boolean;
  isModalConfirmationOpen: boolean;
};

export default function ConsentDocument({
  padding = true,
  onDocumentAction,
  contentBase64,
  isPendingConfirmDocument,
  isPendingRejectDocument,
  isModalConfirmationOpen
}: ConsentDocumentProps) {
  const [searchParams] = useSearchParams();
  const documentConsent = searchParams.get("documentConsent");
  const { t } = useTranslation(["pass"]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isLoadedDocument, setIsLoadedDocument] = useState(false);
  const lastPageRef = useRef<HTMLDivElement>(null);
  const [isScrollExist, setIsScrollExist] = useState(false);
  const [comment, setComment] = useState<string>();

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  useLayoutEffect(() => {
    const contentWrapper = containerRef?.current;

    const updateScrollState = () => {
      contentWrapper && setIsScrollExist(contentWrapper.scrollHeight > contentWrapper.clientHeight);
    };

    updateScrollState();

    const observer = new MutationObserver(() => {
      updateScrollState();
    });

    contentWrapper &&
      observer.observe(contentWrapper, {
        attributes: true,
        childList: true,
        subtree: true
      });

    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  const handleAction = (documentAction: DocumentAction, comment?: string) => {
    onDocumentAction({
      documentAction: documentAction,
      documentConsent: documentConsent ?? "",
      comment: comment
    });
  };

  useEffect(() => {
    setIsConfirmModalOpen(false);
    setComment("");
    scrollToTop();
  }, [contentBase64, documentConsent]);
  useEffect(() => {
    if (!isModalConfirmationOpen && comment && comment.length >= 1) {
      setComment("");
    }
  }, [isModalConfirmationOpen]);
  const descriptionError = () => {
    if (!comment) return t("consent.comment_error");
    if (comment?.length > 255) return t("flow.id_control.input.activity_textarea.error.max_length");
    if (comment?.length < 1) return t("flow.id_control.input.activity_textarea.error.min_length");
  };
  return (
    <div className={twMerge("flex h-screen flex-col", padding ? "" : "-mx-4 -my-4")}>
      <div className="absolute top-0 right-0 pt-4 pr-7">
        <button
          type="button"
          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none dark:bg-slate-800"
          onClick={() => handleAction("cancel")}
        >
          <XMarkIcon className="h-6 w-6 text-gray-500" aria-hidden="true" />
        </button>
      </div>
      <ModalDeleteAlias
        showTrashIcon={false}
        isOpen={!!isConfirmModalOpen || isModalConfirmationOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setComment("");
        }}
        isConfirmLoading={isPendingRejectDocument}
        onValidated={() => {
          if (!comment || comment?.length < 1 || comment?.length > 160)
            return toast.error(t("consent.comment_error"));
          handleAction("REFUSED", comment);
        }}
        buttonCancelLabel={t("consent.confirm_rejection")}
      >
        <Base>{t("consent.confirm_cancel")}</Base>

        <InputLabel
          placeholder={t("consent.reject_textatea_placeholder")}
          value={comment}
          onChange={(value) => setComment(value)}
          required={true}
          rows={4}
          minLength={1}
          maxLength={255}
          error={descriptionError()}
        />
      </ModalDeleteAlias>
      <div
        className="flex flex-1 flex-col items-center overflow-y-scroll bg-gray-900 p-3"
        style={{
          scrollbarColor: "#00afeb oklch(96.7% 0.003 264.542)"
        }}
        data-cy="scrollable-container"
        ref={containerRef}
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLInputElement;
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 && scrollTop > 0;
          setHasScrolledToBottom(isAtBottom);
        }}
      >
        <Document
          file={`data:application/pdf;base64,${contentBase64}`}
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages);
            setIsLoadedDocument(true);
          }}
          loading={<Loader />}
          error={<p className="error">{t("consent.failed_load")}</p>}
        >
          {Array.from({ length: numPages }, (_, i) => {
            const pageNumber = i + 1;
            const isLast = pageNumber === numPages;

            return (
              <div key={pageNumber} ref={isLast ? lastPageRef : undefined} className="mb-3">
                <Page pageNumber={pageNumber} width={Math.min(window.innerWidth - 32, 800)} />
              </div>
            );
          })}
        </Document>
      </div>
      <div className="flex justify-end gap-3 border-t bg-white p-3">
        <Button
          className="w-40"
          dataCy="sign-consent-cancel"
          theme={Themes.gray}
          onClick={() => handleAction("cancel")}
          size="md"
          loading={isPendingConfirmDocument || !isLoadedDocument}
        >
          {t("consent.ignore_button")}
        </Button>
        <Button
          className="w-40"
          theme={Themes.red}
          dataCy="reject-consent-button"
          onClick={() => {
            setIsConfirmModalOpen(true);
          }}
          disabled={!hasScrolledToBottom && isScrollExist}
          loading={isPendingConfirmDocument || !isLoadedDocument}
          size="md"
        >
          {t("consent.reject_button")}
        </Button>
        <Button
          className="w-40"
          theme={Themes.blue}
          onClick={() => handleAction("SIGNED")}
          dataCy="sign-consent-button"
          disabled={!hasScrolledToBottom && isScrollExist}
          loading={isPendingConfirmDocument || !isLoadedDocument}
          size="md"
        >
          {t("consent.confirm_button")}
        </Button>
      </div>
    </div>
  );
}
